import { mfaCodeSchema, type TotpSetupResponse } from '@mfos/shared';
import { type SubmitEvent, useState } from 'react';
import { Badge } from '../../components/Badge';
import { CheckboxField, TextField } from '../../components/Field';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { CopyButton } from '../../components/CopyButton';
import { ErrorNotice, LoadingNotice, SuccessNotice } from '../../components/ErrorNotice';
import {
  useConfirmTotp,
  useDisableTotp,
  useMfaStatus,
  useRegenerateRecoveryCodes,
  useStartTotpSetup,
} from '../auth/auth.api';
import { phrase } from '../../lib/i18n/runtime';

const CODE_HINT = '6-digit code from your authenticator app, or a recovery code.';
const CODE_ERROR = 'Enter the 6-digit code from your authenticator app or a recovery code.';

function groupSecret(secret: string): string {
  return secret.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
}

export function MfaSection() {
  const status = useMfaStatus();
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);

  return (
    <section className="card stack" aria-labelledby="mfa-heading">
      <h2 id="mfa-heading">{phrase('Two-step verification')}</h2>
      {recoveryCodes !== null ? (
        <RecoveryCodes
          codes={recoveryCodes}
          onDone={() => {
            setRecoveryCodes(null);
          }}
        />
      ) : status.isPending ? (
        <LoadingNotice>{phrase('Loading…')}</LoadingNotice>
      ) : status.isError ? (
        <ErrorNotice error={status.error} />
      ) : status.data.totpEnabled ? (
        <MfaEnabled
          recoveryCodesRemaining={status.data.recoveryCodesRemaining}
          onRecoveryCodes={setRecoveryCodes}
        />
      ) : (
        <MfaSetup onEnabled={setRecoveryCodes} />
      )}
    </section>
  );
}

function MfaSetup({ onEnabled }: { onEnabled: (codes: readonly string[]) => void }) {
  const start = useStartTotpSetup();
  const confirm = useConfirmTotp();
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | undefined>();

  if (setup === null) {
    return (
      <>
        <p>
          <Badge tone="warn" label={phrase('Off')} />{' '}
          {phrase(
            'Protect your account with an authenticator app (for example 1Password, Google Authenticator or Microsoft Authenticator).',
          )}
        </p>
        <ErrorNotice error={start.error} />
        <div className="form-actions">
          <button
            type="button"
            className="button"
            disabled={start.isPending}
            onClick={() => {
              start.mutate(undefined, { onSuccess: setSetup });
            }}
          >
            {start.isPending ? phrase('Preparing…') : phrase('Set up authenticator app')}
          </button>
        </div>
      </>
    );
  }

  function submit(event: SubmitEvent) {
    event.preventDefault();
    const parsed = mfaCodeSchema.safeParse(code);
    if (!parsed.success || !/^\d{6}$/.test(parsed.data)) {
      setCodeError('Enter the 6-digit code shown in your authenticator app.');
      return;
    }
    setCodeError(undefined);
    confirm.mutate(
      { code: parsed.data },
      {
        onSuccess: (result) => {
          onEnabled(result.recoveryCodes);
        },
      },
    );
  }

  return (
    <form onSubmit={submit} noValidate className="stack">
      <ol className="steps">
        <li>
          <p>
            {phrase(
              'Enter this key in your authenticator app (choose “enter a setup key”, time-based):',
            )}
          </p>
          <p className="secret" aria-label={phrase('Setup key')}>
            <code>{groupSecret(setup.secret)}</code>
          </p>
          <CopyButton text={setup.secret.replace(/\s+/g, '')} label={phrase('Copy key')} />
          <p className="meta">
            On a phone with an authenticator app installed you can also{' '}
            <a href={setup.otpauthUri}>{phrase('open the setup link directly')}</a>.
          </p>
        </li>
        <li>
          <TextField
            label={phrase('Code from the app')}
            autoComplete="one-time-code"
            inputMode="numeric"
            value={code}
            onValueChange={setCode}
            error={codeError}
            required
          />
        </li>
      </ol>
      <ErrorNotice error={confirm.error} />
      <div className="form-actions">
        <button type="submit" className="button" disabled={confirm.isPending}>
          {confirm.isPending ? phrase('Verifying…') : phrase('Verify and turn on')}
        </button>
        <button
          type="button"
          className="button button--secondary"
          onClick={() => {
            setSetup(null);
            setCode('');
          }}
        >
          {phrase('Cancel')}
        </button>
      </div>
    </form>
  );
}

function MfaEnabled({
  recoveryCodesRemaining,
  onRecoveryCodes,
}: {
  recoveryCodesRemaining: number;
  onRecoveryCodes: (codes: readonly string[]) => void;
}) {
  const disable = useDisableTotp();
  const regenerate = useRegenerateRecoveryCodes();
  const [dialog, setDialog] = useState<'disable' | 'regenerate' | null>(null);
  const [code, setCode] = useState('');

  function open(kind: 'disable' | 'regenerate') {
    setCode('');
    disable.reset();
    regenerate.reset();
    setDialog(kind);
  }

  const codeValid = () => (mfaCodeSchema.safeParse(code).success ? null : CODE_ERROR);
  const codeField = (
    <TextField
      label={phrase('Authentication code')}
      autoComplete="one-time-code"
      value={code}
      onValueChange={setCode}
      hint={CODE_HINT}
      required
    />
  );

  return (
    <>
      <p>
        <Badge tone="ok" label={phrase('On')} />{' '}
        {phrase('Your account requires a code from your authenticator app at sign-in.')}
      </p>
      <p className={recoveryCodesRemaining <= 2 ? 'notice notice--warn' : 'meta'}>
        {recoveryCodesRemaining <= 2 && <span aria-hidden="true">! </span>}
        Recovery codes remaining: {recoveryCodesRemaining}
      </p>
      {disable.isSuccess && (
        <SuccessNotice>{phrase('Two-step verification was turned off.')}</SuccessNotice>
      )}
      <div className="form-actions">
        <button
          type="button"
          className="button button--secondary"
          onClick={() => {
            open('regenerate');
          }}
        >
          {phrase('Generate new recovery codes')}
        </button>
        <button
          type="button"
          className="button button--danger"
          onClick={() => {
            open('disable');
          }}
        >
          {phrase('Turn off two-step verification')}
        </button>
      </div>

      <ConfirmDialog
        open={dialog === 'regenerate'}
        title={phrase('Generate new recovery codes?')}
        description={phrase('Your current recovery codes will stop working immediately.')}
        confirmLabel={phrase('Generate new codes')}
        isPending={regenerate.isPending}
        error={regenerate.error}
        validateExtra={codeValid}
        onCancel={() => {
          setDialog(null);
        }}
        onConfirm={() => {
          regenerate.mutate(
            { code: code.trim() },
            {
              onSuccess: (result) => {
                setDialog(null);
                onRecoveryCodes(result.recoveryCodes);
              },
            },
          );
        }}
      >
        {codeField}
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'disable'}
        title={phrase('Turn off two-step verification?')}
        description={phrase(
          'Your account will be protected by your password or email links only. Workspaces that require two-step verification will block sensitive work until you turn it on again.',
        )}
        confirmLabel={phrase('Turn off')}
        tone="danger"
        isPending={disable.isPending}
        error={disable.error}
        validateExtra={codeValid}
        onCancel={() => {
          setDialog(null);
        }}
        onConfirm={() => {
          disable.mutate(
            { code: code.trim() },
            {
              onSuccess: () => {
                setDialog(null);
              },
            },
          );
        }}
      >
        {codeField}
      </ConfirmDialog>
    </>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: readonly string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  return (
    <div className="stack">
      <SuccessNotice>
        {phrase('Two-step verification is on. Save your recovery codes now.')}
      </SuccessNotice>
      <p>
        {phrase(
          'Each code works once if you lose access to your authenticator app. They are shown only this time — store them in a password manager or print them.',
        )}
      </p>
      <ul className="recovery-codes" aria-label={phrase('Recovery codes')}>
        {codes.map((code) => (
          <li key={code}>
            <code>{code}</code>
          </li>
        ))}
      </ul>
      <CopyButton text={codes.join('\n')} label={phrase('Copy all codes')} />
      <CheckboxField
        label={phrase('I have saved these codes')}
        checked={saved}
        onCheckedChange={setSaved}
      />
      <div className="form-actions">
        <button type="button" className="button" disabled={!saved} onClick={onDone}>
          {phrase('Done')}
        </button>
      </div>
    </div>
  );
}
